import React, { useMemo, useState } from 'react';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CssBaseline,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { images } from '../../shared/images/images';
import logoNuevo from '../../shared/images/logoEmcali.jpg';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { getProyectosApiBaseUrl } from '../../utils/proyectosApiBaseUrl';
import {
  clearAuthSession,
  clearLoginRemember,
  loadLoginRemember,
  saveLoginRemember,
} from '../../utils/loginRemember';

const validationLogin = Yup.object({
  username: Yup.string().required('El nombre de usuario es obligatorio'),
  password: Yup.string().required('La contraseña es obligatoria'),
});

const theme = createTheme({
  palette: {
    primary: { main: '#0a2f57' },
  },
  shape: { borderRadius: 14 },
});

const SignInSide: React.FC = () => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const remembered = useMemo(() => loadLoginRemember(), []);

  const encodeBase64 = (data: string) => btoa(data);
  const normalizeRole = (value: string) => {
    const normalized = String(value || "").toLowerCase().trim();
    if (normalized.includes("admin")) return "administrador";
    if (normalized.includes("proyet")) return "proyectos";
    return normalized;
  };

  const handleSubmit = async (
    values: { username: string; password: string; remember: boolean },
    { setSubmitting, setErrors }: { setSubmitting: (isSubmitting: boolean) => void; setErrors: (errors: { [key: string]: string }) => void; },
  ) => {
    try {
      const response = await fetch(`${getProyectosApiBaseUrl()}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          usuario: values.username,
          contraseña: values.password,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        const msg = Array.isArray(data.message)
          ? data.message.join(' ')
          : data.message || 'Usuario o contraseña inválidos.';
        throw new Error(msg);
      }

      if (values.remember) {
        saveLoginRemember(values.username, values.password);
      } else {
        clearLoginRemember();
      }

      clearAuthSession();
      localStorage.setItem('authToken', encodeBase64(data.token));
      localStorage.setItem('user', encodeBase64(data.usuario));

      const cargoId =
        data?.id_cargo ??
        data?.idCargo ??
        data?.cargo_id ??
        data?.cargo?.id ??
        data?.rol?.id;
      if (cargoId !== undefined && cargoId !== null) {
        localStorage.setItem('CargoId', encodeBase64(String(cargoId)));
      }

      const rolRaw =
        (typeof data.rol === 'string' ? data.rol : '') ||
        data?.rol?.nombre ||
        data?.rol?.descripcion ||
        data?.cargo?.descripcion ||
        data?.perfil?.descripcion ||
        'proyectos';
      localStorage.setItem('Role', encodeBase64(normalizeRole(rolRaw)));

      navigate('/dashboard');
    } catch (error: any) {
      setErrors({ username: error.message });
      setErrorMessage(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Grid
        container
        component="main"
        sx={{
          minHeight: '100vh',
          backgroundImage: `linear-gradient(rgba(10,47,87,.50), rgba(10,47,87,.70)), url(${images.SliderLogin})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <CssBaseline />
        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <Paper elevation={8} sx={{ width: '100%', maxWidth: 460, p: 4, borderRadius: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Box component="img" sx={{ width: 180, objectFit: 'contain' }} alt="Logo Emcali" src={logoNuevo} />
              <Typography variant="h5" fontWeight={700} color="primary.main">
                Bienvenido
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use su usuario de red EMCALI. Si no puede ingresar por directorio activo, se validará con su
                usuario interno del módulo.
              </Typography>
            </Box>

            <Formik
              initialValues={{
                username: remembered.username,
                password: remembered.password,
                remember: remembered.remember,
              }}
              validationSchema={validationLogin}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, isSubmitting, isValid, values, setFieldValue }) => (
                <Form>
                  <Field
                    as={TextField}
                    margin="normal"
                    required
                    fullWidth
                    label="Usuario"
                    name="username"
                    autoComplete="username"
                    autoFocus
                    helperText={<ErrorMessage name="username" />}
                    error={Boolean(touched.username && errors.username)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Field
                    as={TextField}
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Contraseña"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    helperText={<ErrorMessage name="password" />}
                    error={Boolean(touched.password && errors.password)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                            {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!values.remember}
                          onChange={(e) => setFieldValue('remember', e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Recuérdame</Typography>}
                    />
                    <Link component={RouterLink} to="/olvido-contrasena" variant="body2" underline="hover">
                      ¿Olvidó su contraseña?
                    </Link>
                  </Box>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 2.5, py: 1.2, fontWeight: 700, textTransform: 'none' }}
                    disabled={!isValid || isSubmitting}
                  >
                    {isSubmitting ? 'Ingresando...' : 'Iniciar Sesión'}
                  </Button>
                </Form>
              )}
            </Formik>
          </Paper>
        </Grid>

        <Snackbar
          open={!!errorMessage}
          autoHideDuration={6000}
          onClose={() => setErrorMessage(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: '100%' }}>
            {errorMessage}
          </Alert>
        </Snackbar>
      </Grid>
    </ThemeProvider>
  );
};

export default SignInSide;





